import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "../data/content";
import { useHub } from "../lib/hubContent";
import { EditSectionButton } from "./HubEditor";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";

const TILE_IMAGES = [
  { src: IMAGES.beautyJersey, alt: "Nelle in a blue 17 jersey, white tiered skirt and cargos", rotate: "-rotate-2", offset: "" },
  { src: IMAGES.beautyMosaic, alt: "Nelle in an orange floral top, green trousers and fuzzy orange slides against a mosaic wall", rotate: "rotate-1", offset: "md:mt-12" },
  { src: IMAGES.beautyAfterHours, alt: "Nelle in purple after-hours light, mid-thought", rotate: "rotate-2", offset: "md:mt-6" },
];

export default function BeautyStyle() {
  const c = useHub().section("beauty");
  const captions = c.captions || [];
  return (
    <section id="beauty" data-testid="beauty-section" className="relative bg-[#F9E9EF] text-[#831843] py-24 md:py-32 overflow-hidden">
      <EditSectionButton sectionKey="beauty" label="Edit" />
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          eyebrow={c.eyebrow}
          title={<>Beauty <span className="italic text-en-pink">+</span> Style</>}
          note={c.note}
          noteColor="text-en-purple"
        />

        <div className="grid md:grid-cols-3 gap-8 md:gap-6 items-start mb-16">
          {TILE_IMAGES.map((t, i) => (
            <Reveal key={t.src} delay={i * 0.12} className={t.offset}>
              <figure className={`group relative bg-white p-3 pb-12 shadow-xl ${t.rotate} hover:rotate-0 hover:scale-[1.02] transition-all duration-500`}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-white/70 backdrop-blur border border-black/5 rotate-2 z-10" aria-hidden="true" />
                <img src={t.src} alt={t.alt} className="w-full aspect-[4/5] object-cover" loading="lazy" />
                <figcaption className="absolute bottom-3 left-0 right-0 text-center font-hand text-xl text-en-ink/75">
                  {captions[i] || ""}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-pink-900/50 mb-4">{c.palette_label}</p>
              <div data-testid="beauty-swatches" className="flex rounded-2xl overflow-hidden border border-pink-200 shadow-sm w-max">
                {(c.swatches || []).map((s) => (
                  <div key={s.name} className="group relative w-10 h-14 md:w-14 md:h-16 cursor-crosshair" style={{ backgroundColor: s.hex }} title={s.name}>
                    <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap font-mono text-[8px] tracking-[0.15em] uppercase text-pink-900/60 opacity-0 group-hover:opacity-100 transition-opacity">
                      {s.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <a
              href={c.lookbook_url || "#beauty"}
              target={c.lookbook_url ? "_blank" : undefined}
              rel={c.lookbook_url ? "noopener noreferrer" : undefined}
              data-testid="beauty-lookbook-button"
              onClick={c.lookbook_url ? undefined : (e) => { e.preventDefault(); toast(c.lookbook_toast, { description: c.lookbook_toast_detail }); }}
              className="group inline-flex items-center gap-2 self-start rounded-full bg-en-pink text-white px-7 py-3.5 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-en-ink transition-colors"
            >
              Open the lookbook
              <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
